import { Component, OnInit, NgZone, OnDestroy } from '@angular/core';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus, type AuthenticationResult } from '@azure/msal-browser';
import { filter, take, Subject, takeUntil } from 'rxjs';
import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  template: `
    <div class="loading-screen" *ngIf="isLoading">
      <i class="pi pi-spin pi-spinner loading-spinner"></i>
      <p>Signing in&hellip;</p>
    </div>

    <div class="app-wrapper" *ngIf="!isLoading && isAuthenticated">
      <app-sidebar></app-sidebar>
      <div class="main-content">
        <router-outlet></router-outlet>
      </div>
    </div>

    <p-toast></p-toast>
  `,
  styles: [`
    .loading-screen {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100vh;
      gap: 1rem;
      background: var(--surface-ground);
      color: var(--text-color-secondary);
    }
    .loading-spinner {
      font-size: 3rem;
      color: var(--primary-color);
    }
    .app-wrapper {
      display: flex;
      height: 100vh;
      overflow: hidden;
    }
    .main-content {
      flex: 1;
      height: 100vh;
      overflow-y: auto;
    }
  `]
})
export class AppComponent implements OnInit, OnDestroy {
  isAuthenticated = false;
  isLoading = true;

  private readonly scopes = [`api://${environment.backendClientId}/user_impersonation`];
  private readonly destroy$ = new Subject<void>();

  constructor(
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private ngZone: NgZone
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    void this.msalService.instance
      .handleRedirectPromise()
      .then((result) => this.onRedirectHandled(result))
      .catch((err: unknown) => {
        console.error('MSAL handleRedirectPromise failed', err);
        this.clearMsalSessionScratchKeys();
        this.scheduleLoginRedirect();
      });
  }

  private onRedirectHandled(result: AuthenticationResult | null): void {
    const account =
      result?.account ??
      this.msalService.instance.getActiveAccount() ??
      this.msalService.instance.getAllAccounts()[0];

    if (account) {
      this.msalService.instance.setActiveAccount(account);
      this.ngZone.run(() => {
        this.isAuthenticated = true;
        this.isLoading = false;
      });
      return;
    }

    // Defer: handleRedirectPromise can settle in the same turn MSAL still marks
    // interaction_in_progress; calling loginRedirect immediately throws (Safari/Zone).
    this.scheduleLoginRedirect();
  }

  /** Wait for MSAL to be idle, then start login redirect. */
  private scheduleLoginRedirect(): void {
    // Clear any stale interaction flag
    this.clearInteractionInProgressFlag();

    // Wait for MSAL's inProgress$ to emit None before attempting login
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status) => status === InteractionStatus.None),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        // Extra delay for Safari/Zone.js timing
        setTimeout(() => this.beginLoginRedirect(0), 50);
      });

    // Fallback: if inProgress$ never emits None within 2s, force login anyway
    setTimeout(() => {
      if (this.isLoading && !this.isAuthenticated) {
        console.warn('MSAL inProgress$ timeout - forcing login redirect');
        this.clearInteractionInProgressFlag();
        this.beginLoginRedirect(0);
      }
    }, 2000);
  }

  private beginLoginRedirect(attempt: number): void {
    void this.msalService.instance.loginRedirect({ scopes: this.scopes }).catch((err: unknown) => {
      const errorCode =
        typeof err === 'object' && err !== null && 'errorCode' in err
          ? String((err as { errorCode: string }).errorCode)
          : '';
      if (errorCode === 'interaction_in_progress' && attempt < 3) {
        console.warn(`loginRedirect blocked by interaction_in_progress (attempt ${attempt + 1}), clearing and retrying...`);
        this.clearInteractionInProgressFlag();
        const delay = [100, 300, 600][attempt] ?? 600;
        setTimeout(() => this.beginLoginRedirect(attempt + 1), delay);
        return;
      }
      console.error('loginRedirect failed', err);
      this.ngZone.run(() => {
        this.isLoading = false;
      });
    });
  }

  /** Remove the msal.interaction.status key from sessionStorage AND cookies. */
  private clearInteractionInProgressFlag(): void {
    sessionStorage.removeItem('msal.interaction.status');
    localStorage.removeItem('msal.interaction.status');
    // Also clear cookie (MSAL sets one when storeAuthStateInCookie is true)
    // Try multiple path variants for Safari compatibility
    document.cookie = 'msal.interaction.status=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/';
    document.cookie = 'msal.interaction.status=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie = 'msal.interaction.status=; max-age=0; path=/';
  }

  private clearMsalSessionScratchKeys(): void {
    for (const key of Object.keys(sessionStorage)) {
      if (key.startsWith('msal.')) {
        sessionStorage.removeItem(key);
      }
    }
    // Clear msal cookies too
    document.cookie.split(';').forEach((c) => {
      const name = c.split('=')[0].trim();
      if (name.startsWith('msal.')) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      }
    });
  }
}
